import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { colors, typography } from '../styles/theme';
import config from '../config';
import { displayEntityType } from '../utils/entityTerminology';

const AvatarFrame = styled.span<{ $size: number }>`
  width: ${({ $size }) => $size}px;
  height: ${({ $size }) => $size}px;
  min-width: ${({ $size }) => $size}px;
  border-radius: 6px;
  overflow: hidden;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  background: ${colors.background.tertiary};
  border: 1px solid ${colors.border.light};
  color: ${colors.text.secondary};
  font-size: ${({ $size }) => Math.max(10, Math.round($size * 0.34))}px;
  font-weight: ${typography.fontWeights.semibold};
  line-height: 1;
  flex: 0 0 auto;
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  display: block;
  object-fit: contain;
  background: ${colors.white};
`;

function initials(name: string, entityType?: string | null): string {
  const words = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (words.length > 0) {
    return words.slice(0, 2).map(word => word[0]).join('').toUpperCase();
  }
  return String(entityType || 'Entity').slice(0, 2).toUpperCase();
}

export interface EntityAvatarProps {
  name: string;
  entityType?: string | null;
  logoUrl?: string | null;
  size?: number;
  lazy?: boolean;
  className?: string;
}

export const EntityAvatar: React.FC<EntityAvatarProps> = ({
  name,
  entityType,
  logoUrl,
  size = 40,
  lazy = true,
  className,
}) => {
  const [failed, setFailed] = useState(false);
  useEffect(() => setFailed(false), [logoUrl]);
  const showImage = config.features.entityLogos && Boolean(logoUrl) && !failed;
  const fallbackLabel = name || displayEntityType(entityType) || 'Entity';

  return (
    <AvatarFrame
      $size={size}
      className={className}
      role={showImage ? undefined : 'img'}
      aria-label={showImage ? undefined : `${fallbackLabel} logo placeholder`}
      title={fallbackLabel}
    >
      {showImage ? (
        <AvatarImage
          src={logoUrl!}
          alt={`${name} logo`}
          width={size}
          height={size}
          loading={lazy ? 'lazy' : 'eager'}
          decoding="async"
          onError={() => setFailed(true)}
        />
      ) : initials(name, fallbackLabel)}
    </AvatarFrame>
  );
};

export default EntityAvatar;